<template>
  <div class="exit-quality">
    <apexchart
      ref="dailyChart"
      height="520"
      :options="dailyOptions"
      :series="dailySeries"
    />
    <apexchart
      height="420"
      :options="histOptions"
      :series="histSeries"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { computeExitQuality, bandIdForMargin } from '@/utils/computeExitQuality.js';
import {
  categoryXAxisOptions,
  latestCategoryZoom,
  manualChartPanOptions,
} from '@/utils/manualChartDays.js';

const MANUAL_SELL_COLOR = '#e66801';
const HEDGE_BUY_COLOR = '#38bdf8';
const DAILY_CHART_ID = 'evol-exit-quality';

function fmtUSD(value) {
  const amount = Number(value) || 0;
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtSignedUSD(value) {
  const amount = Number(value) || 0;
  const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (amount > 0) return `+$${formatted}`;
  if (amount < 0) return `-$${formatted}`;
  return `$${formatted}`;
}

function fmtPct(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function marginColor(value, target, proximity) {
  if (value == null || !Number.isFinite(value)) return '#94a3b8';
  const band = bandIdForMargin(value, target, proximity);
  if (band === 'onTarget') return '#4ade80';
  if (band === 'above') return '#38bdf8';
  return '#f87171';
}

function chartFromContext(component, chartContext) {
  return chartContext
    || component.$refs?.dailyChart?.chart
    || component.$refs?.dailyChart
    || null;
}

export default {
  name: 'EvolExitQualityChart',
  props: {
    logs: { type: Array, default: () => [] },
    rawData: { type: Array, default: () => [] },
    aggregatedData: { type: Array, default: () => [] },
  },
  computed: {
    ...mapState(['targetMarginPct']),

    allLogs() {
      if (this.logs && this.logs.length > 0) return this.logs;
      if (this.rawData && this.rawData.length > 0) return this.rawData;
      if (this.aggregatedData && this.aggregatedData.length > 0) return this.aggregatedData;
      return this.$store?.state?.eventLogs || [];
    },

    quality() {
      return computeExitQuality(this.allLogs, { targetMarginPct: this.targetMarginPct });
    },

    daySignature() {
      const days = this.quality.days;
      if (!days.length) return '';
      return `${days.length}:${days[0].dayKey}:${days[days.length - 1].dayKey}`;
    },

    dailySeries() {
      const target = this.quality.targetMarginPct;
      return [
        { name: 'Ponderada', data: this.quality.days.map((day) => day.weightedPct) },
        { name: 'Mediana', data: this.quality.days.map((day) => day.medianPct) },
        { name: `Alvo ${target.toFixed(2)}%`, data: this.quality.days.map(() => target) },
      ];
    },

    dailyYMin() {
      const values = this.quality.days.flatMap((day) => [day.weightedPct, day.medianPct])
        .filter((value) => Number.isFinite(value));
      const dataMin = values.length ? Math.min(...values) : 0;
      return Math.min(0, dataMin);
    },

    histSeries() {
      return [
        { name: 'Close Long', data: this.quality.histogram.map((band) => band.closeLong.notional) },
        { name: 'Close Short', data: this.quality.histogram.map((band) => band.closeShort.notional) },
      ];
    },

    histYMax() {
      const peak = Math.max(0, ...this.quality.histogram.map((band) => band.notional));
      if (peak <= 0) return 1;
      return peak * 1.35;
    },

    dailyOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          ...manualChartPanOptions(),
          id: DAILY_CHART_ID,
          type: 'line',
          events: {
            mounted(chartContext) {
              self.applyLatestXWindow(chartContext);
            },
            beforeResetZoom() {
              return self.latestResetZoomWindow();
            },
          },
        },
        title: {
          text: 'Qualidade de saída',
          align: 'left',
          style: { color: '#f1f1f1', fontSize: '14px', fontWeight: 600 },
        },
        subtitle: {
          text: 'Σ PnL / Σ notional de saída · linha no alvo vigente',
          align: 'left',
          style: { color: '#94a3b8', fontSize: '11px' },
        },
        colors: ['#f8fafc', '#c084fc', '#fbbf24'],
        fill: { opacity: 1 },
        stroke: { curve: 'straight', width: [3, 2, 1.5], dashArray: [0, 0, 6] },
        markers: { size: [4, 3, 0], hover: { size: 5 }, strokeWidth: 0 },
        xaxis: categoryXAxisOptions(this.quality.days.map((day) => day.label)),
        yaxis: {
          min: this.dailyYMin,
          tickAmount: 6,
          decimalsInFloat: 2,
          title: { text: 'Margem (%)' },
          labels: { formatter: (value) => `${Number(value || 0).toFixed(2)}%` },
        },
        annotations: {
          yaxis: [{
            y: this.quality.onTargetLowPct,
            y2: this.quality.onTargetHighPct,
            fillColor: '#4ade80',
            opacity: 0.12,
            borderColor: 'transparent',
          }],
        },
        legend: { ...defaultChartOptions.legend },
        tooltip: {
          shared: true,
          intersect: false,
          custom({ dataPointIndex }) {
            return self.dailyTooltip(dataPointIndex);
          },
        },
        noData: {
          text: 'Sem fechamento no período',
          style: { color: '#94a3b8' },
        },
      };
    },

    histOptions() {
      const self = this;
      const { onTargetLowPct, onTargetHighPct, eventCount } = this.quality;
      const cuts = `Abaixo < ${onTargetLowPct.toFixed(2)}% · no alvo ${onTargetLowPct.toFixed(2)}–${onTargetHighPct.toFixed(2)}% · acima > ${onTargetHighPct.toFixed(2)}%`;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          id: 'evol-exit-quality-bands',
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        title: {
          text: 'Faixas de margem do período',
          align: 'left',
          style: { color: '#f1f1f1', fontSize: '14px', fontWeight: 600 },
        },
        subtitle: {
          text: eventCount ? cuts : 'Nenhum fechamento com notional positivo no período',
          align: 'left',
          style: { color: '#94a3b8', fontSize: '11px' },
        },
        colors: [MANUAL_SELL_COLOR, HEDGE_BUY_COLOR],
        fill: { opacity: 0.9 },
        xaxis: {
          categories: this.quality.histogram.map((band) => band.label),
          labels: { style: { colors: '#f1f1f1' } },
        },
        yaxis: {
          min: 0,
          max: this.histYMax,
          tickAmount: 4,
          decimalsInFloat: 0,
          title: { text: 'USD de saída' },
          labels: { formatter: (value) => fmtUSD(value) },
        },
        plotOptions: {
          bar: {
            columnWidth: '48%',
            dataLabels: {
              total: {
                enabled: true,
                offsetY: -8,
                style: { fontSize: '12px', fontWeight: 600, color: '#f1f1f1' },
                formatter(value, opts) {
                  const band = self.quality.histogram[opts.dataPointIndex];
                  if (!band || !band.count) return '';
                  return `${band.count} · ${fmtUSD(value)}`;
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter() {
            return '';
          },
        },
        legend: { ...defaultChartOptions.legend },
        tooltip: {
          shared: true,
          intersect: false,
          custom({ dataPointIndex }) {
            return self.histTooltip(dataPointIndex);
          },
        },
      };
    },
  },
  watch: {
    daySignature() {
      this.$nextTick(() => this.applyLatestXWindow());
    },
  },
  methods: {
    applyLatestXWindow(chartContext) {
      const chart = chartFromContext(this, chartContext);
      const zoom = latestCategoryZoom(this.quality.days.length);
      if (!chart || !zoom || typeof chart.zoomX !== 'function') return;
      chart.zoomX(zoom.start, zoom.end);
    },
    latestResetZoomWindow() {
      const zoom = latestCategoryZoom(this.quality.days.length);
      if (!zoom) return undefined;
      return { xaxis: { min: zoom.start, max: zoom.end } };
    },
    dailyTooltip(dataPointIndex) {
      const day = this.quality.days[dataPointIndex];
      if (!day) return '';
      const { targetMarginPct, proximityPct } = this.quality;
      const legLine = (label, color, leg) => {
        if (!leg.count) return '';
        return `<div style="padding-left: 10px; line-height: 1.5; color: ${color};">${label}: <b>${leg.count}</b> · <b>${fmtUSD(leg.notional)}</b> · <b>${fmtPct(leg.weightedPct)}</b></div>`;
      };
      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 240px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #38bdf8;">
            ${day.label}
          </div>
          <div style="line-height: 1.6;">
            Ponderada: <b style="color: ${marginColor(day.weightedPct, targetMarginPct, proximityPct)}">${fmtPct(day.weightedPct)}</b><br/>
            Mediana: <b style="color: ${marginColor(day.medianPct, targetMarginPct, proximityPct)}">${fmtPct(day.medianPct)}</b><br/>
            Alvo vigente: <b style="color: #fbbf24">${targetMarginPct.toFixed(2)}%</b>
          </div>
          <div style="border-top: 1px solid #444; margin-top: 8px; padding-top: 6px;">
            ${day.count
              ? `${day.count} eventos · ${fmtUSD(day.notional)} · PnL <b style="color: ${day.pnl >= 0 ? '#4ade80' : '#f87171'}">${fmtSignedUSD(day.pnl)}</b>`
              : 'Sem fechamento neste dia'}
            ${legLine('Close Long', MANUAL_SELL_COLOR, day.closeLong)}
            ${legLine('Close Short', HEDGE_BUY_COLOR, day.closeShort)}
          </div>
        </div>
      `;
    },
    histTooltip(dataPointIndex) {
      const band = this.quality.histogram[dataPointIndex];
      if (!band) return '';
      const { onTargetLowPct, onTargetHighPct } = this.quality;
      const low = onTargetLowPct.toFixed(2);
      const high = onTargetHighPct.toFixed(2);
      const rule = {
        below: `< ${low}%`,
        onTarget: `${low}% até ${high}%`,
        above: `> ${high}%`,
      }[band.id];
      const legLine = (label, color, leg) => `
        <div style="color: ${color}; line-height: 1.5;">${label}: <b>${leg.count}</b> eventos · <b>${fmtUSD(leg.notional)}</b></div>
      `;
      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 230px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px;">
            ${band.label} <span style="color: #94a3b8; font-weight: 500;">${rule}</span>
          </div>
          ${legLine('Close Long', MANUAL_SELL_COLOR, band.closeLong)}
          ${legLine('Close Short', HEDGE_BUY_COLOR, band.closeShort)}
          <div style="border-top: 1px solid #444; margin-top: 8px; padding-top: 6px;">
            Faixa: <b>${band.count}</b> eventos · <b>${fmtUSD(band.notional)}</b>
          </div>
        </div>
      `;
    },
  },
};
</script>

<style scoped>
.exit-quality {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}
</style>
